import { PackageCheck, Plus } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "../components/EmptyState";
import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { matchesCollectionSearch } from "../lib/collection-search";
import {
  collectionRecordSearchValues,
  formatQuantity,
  purchaseOrderColumns,
  toPurchaseOrderRecord,
} from "../lib/purchasing-collection-records";
import { isOfflineQueued } from "../lib/offline-link";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";

const statusLabels = {
  draft: "Bozza",
  email_prepared: "Email pronta",
  sent: "Inviato",
  partially_received: "Arrivato in parte",
  received: "Ricevuto",
  cancelled: "Annullato",
} as const;

export function PurchaseOrdersPage() {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const orders = trpc.purchasing.orders.list.useQuery();
  const requests = trpc.purchasing.requests.list.useQuery({ status: "requested" });
  const suppliers = trpc.suppliers.list.useQuery();
  const sites = trpc.sites.list.useQuery();
  const offers = trpc.materials.listOffers.useQuery();
  const createOrder = trpc.purchasing.orders.create.useMutation();
  const [viewMode, setViewMode] = useViewMode("ordini-acquisto");
  const [open, setOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | keyof typeof statusLabels>("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");
  const orderableRequests = requests.data?.filter((request) => request.materialId) ?? [];
  const filteredOrders = useMemo(() => (orders.data ?? []).filter((order) => {
    const orderWithSites = order as typeof order & { siteIds?: string[] };
    const createdAt = new Date(order.createdAt);
    const from = fromFilter ? new Date(`${fromFilter}T00:00:00`) : null;
    const to = toFilter ? new Date(`${toFilter}T23:59:59.999`) : null;
    return (statusFilter === "all" || order.status === statusFilter)
      && (supplierFilter === "all" || order.supplierId === supplierFilter)
      && (siteFilter === "all" || orderWithSites.siteIds?.includes(siteFilter))
      && (!from || createdAt >= from)
      && (!to || createdAt <= to)
      && matchesCollectionSearch(search, collectionRecordSearchValues(toPurchaseOrderRecord(order)));
  }), [fromFilter, orders.data, search, siteFilter, statusFilter, supplierFilter, toFilter]);
  const orderRecords = filteredOrders.map(toPurchaseOrderRecord);
  const hasActiveFilters = Boolean(
    search.trim()
    || statusFilter !== "all"
    || supplierFilter !== "all"
    || siteFilter !== "all"
    || fromFilter
    || toFilter,
  );

  function toggleRequest(id: string) {
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!navigator.onLine) {
      setNotice("Per creare l'ordine serve una connessione. Le richieste restano salvate.");
      return;
    }
    const data = new FormData(event.currentTarget);
    // Il fornitore si rilegge dal modulo, non dallo stato: vedere `valoreDelCampo`.
    const fornitoreScelto = String(data.get("supplierId") || "") || supplierId;
    if (!fornitoreScelto) {
      setNotice("Scegli il fornitore prima di creare l'ordine.");
      return;
    }
    if (selectedIds.length === 0) {
      setNotice("Seleziona almeno una richiesta.");
      return;
    }
    try {
      const order = await createOrder.mutateAsync({
        supplierId: fornitoreScelto,
        notes: String(data.get("notes") || "") || null,
        lines: selectedIds.map((requestId) => ({
          requestId,
          supplierOfferId: String(data.get(`offer-${requestId}`) || "") || null,
          packagesOrdered: Number(data.get(`packages-${requestId}`)) || null,
          orderedQuantity: Number(data.get(`quantity-${requestId}`)),
          notes: String(data.get(`notes-${requestId}`) || "") || null,
        })),
      });
      if (isOfflineQueued(order)) {
        // Il numero dell'ordine lo assegna il server: finche non lo ha visto non c'e
        // scheda da aprire, e portarci l'utente mostrerebbe una pagina che non esiste.
        setNotice("Ordine salvato sul dispositivo. Parte da solo appena torna la rete.");
        return;
      }
      await Promise.all([
        utils.purchasing.orders.list.invalidate(),
        utils.purchasing.requests.list.invalidate(),
      ]);
      navigate(`/acquisti/ordini/${order.id}`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Ordine non creato.");
    }
  }

  return (
    <section className="purchase-page">
      <header className="page-heading">
        <div><p className="eyebrow">Acquisti raggruppati</p><h1>Ordini</h1><p>Unisci richieste di più strutture in un solo ordine al fornitore.</p></div>
        <div className="page-heading__actions"><ViewModeToggle value={viewMode} onChange={setViewMode} /><button className="button button--primary" type="button" onClick={() => setOpen(!open)}><Plus size={18} /> Nuovo ordine</button></div>
      </header>

      {notice && <p className="notice" role="status">{notice}</p>}

      <div className="purchase-filters purchase-filters--orders">
        <CollectionSearch label="Cerca ordini" value={search} onChange={setSearch} placeholder="Numero, fornitore, struttura, materiale o note" />
        <label><span>Stato ordine</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">Tutti</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label><span>Fornitore ordini</span><select value={supplierFilter} onChange={(event) => setSupplierFilter(event.target.value)}><option value="all">Tutti</option>{suppliers.data?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>
        <label><span>Struttura ordini</span><select value={siteFilter} onChange={(event) => setSiteFilter(event.target.value)}><option value="all">Tutte</option>{sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
        <label><span>Ordini dal</span><input type="date" value={fromFilter} onChange={(event) => setFromFilter(event.target.value)} /></label>
        <label><span>Ordini al</span><input type="date" value={toFilter} onChange={(event) => setToFilter(event.target.value)} /></label>
      </div>

      {open && (
        <form className="editor-card order-composer" onSubmit={submit}>
          <div className="editor-card__heading"><h2>Prepara ordine</h2><p>Seleziona il fornitore e tutte le richieste da raggruppare.</p></div>
          <label className="order-composer__supplier"><span>Fornitore ordine</span><select name="supplierId" value={supplierId} onChange={(event) => setSupplierId(event.target.value)}><option value="">Seleziona fornitore</option>{suppliers.data?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>
          <div className="order-request-picker">
            {orderableRequests.map((request) => {
              const selected = selectedIds.includes(request.id);
              const requestOffers = offers.data?.filter((offer) => offer.supplierId === supplierId && offer.materialId === request.materialId) ?? [];
              const label = request.materialName || request.freeLabel || "Materiale";
              return (
                <article className={selected ? "is-selected" : ""} key={request.id}>
                  <label className="order-request-picker__check"><input type="checkbox" checked={selected} onChange={() => toggleRequest(request.id)} aria-label={`${label} · ${request.siteName} · ${formatQuantity(request.requestedQuantity, request.unit)}`} /><span><strong>{label}</strong><small>{request.siteName}</small></span></label>
                  <label><span>Quantità</span><input name={`quantity-${request.id}`} type="number" min="0.0001" step="any" required={selected} disabled={!selected} defaultValue={Number(request.requestedQuantity)} /></label>
                  <label><span>Prezzo fornitore</span><select name={`offer-${request.id}`} disabled={!selected} defaultValue=""><option value="">Senza listino</option>{requestOffers.map((offer) => <option key={offer.id} value={offer.id}>{offer.packageDescription}</option>)}</select></label>
                  <label><span>Confezioni</span><input name={`packages-${request.id}`} type="number" min="1" step="1" disabled={!selected} /></label>
                  <label><span>Nota riga</span><input name={`notes-${request.id}`} disabled={!selected} /></label>
                </article>
              );
            })}
          </div>
          {orderableRequests.length === 0 && <EmptyState title="Nessuna richiesta ordinabile">Le richieste libere devono prima essere collegate al catalogo.</EmptyState>}
          <label className="order-composer__notes"><span>Note generali ordine</span><textarea name="notes" rows={3} /></label>
          {createOrder.error && <p className="form-error" role="alert">{createOrder.error.message}</p>}
          <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setOpen(false)}>Annulla</button><button className="button button--primary" disabled={createOrder.isPending}>Crea ordine</button></div>
        </form>
      )}

      {orders.error && <div className="query-error" role="alert"><strong>Ordini non disponibili</strong><span>{orders.error.message}</span><button className="button button--outline" type="button" onClick={() => void orders.refetch()}>Riprova</button></div>}
      {!orders.error && orderRecords.length > 0 && <CollectionView
        mode={viewMode}
        ariaLabel="Ordini"
        titleLabel="Ordine"
        columns={purchaseOrderColumns}
        records={orderRecords}
        print={{
          section: "Ordini",
          details: [
            statusFilter === "all" ? null : `Stato: ${statusLabels[statusFilter]}`,
            supplierFilter === "all" ? null : `Fornitore: ${suppliers.data?.find((fornitore) => fornitore.id === supplierFilter)?.name ?? ""}`,
            siteFilter === "all" ? null : `Struttura: ${sites.data?.find((struttura) => struttura.id === siteFilter)?.name ?? ""}`,
            fromFilter || toFilter ? `Periodo: ${fromFilter || "inizio"} - ${toFilter || "oggi"}` : null,
            search ? `Ricerca: ${search}` : null,
          ],
        }}
      />}
      {!orders.error && !orders.isLoading && orderRecords.length === 0 && (hasActiveFilters
        ? <EmptyState title="Nessun risultato">Modifica la ricerca o i filtri per vedere altri ordini.</EmptyState>
        : <EmptyState title="Nessun ordine">Le richieste raggruppate compariranno qui.</EmptyState>)}
      {orders.isLoading && <p className="loading-copy"><PackageCheck size={18} /> Caricamento ordini...</p>}
    </section>
  );
}
